import { useEffect, useRef } from 'react';
import { getUtmMetadata, initAnalytics, track } from '../utils/telemetry';
import './Box3D.css';

const GITHUB_URL = 'https://github.com/swapp1990/three-box3d';
const GOTCHAS_URL =
  'https://github.com/swapp1990/three-box3d/blob/main/docs-site/guide/gotchas.md';

/** Confirmed in-app box3d dogfood routes (App.tsx PAGE_PATH). */
const DEMO_ROUTES = [
  {
    path: '/wrecking-yard',
    label: 'Wrecking Yard',
    blurb: 'Demolition + brick structures on live box3d WASM.',
  },
  {
    path: '/physics-playground',
    label: 'Physics Playground',
    blurb: 'Flick, throw, blast — fixed-step runtime playground.',
  },
  {
    path: '/worldcup',
    label: 'World Cup',
    blurb: 'Stadium shootout dogfood scene.',
  },
  {
    path: '/humanoid-lab',
    label: 'Humanoid Lab',
    blurb: 'Procedural ragdoll: drop, stairs, hit, drag, pile, active balance.',
  },
] as const;

const QUICKSTART_SNIPPET = `import { createBox3D, FixedStepper, TransformBuffer, SleepManager } from 'box3d-web';
import { writeTransformsToInstancedMesh } from 'three-box3d';
import * as THREE from 'three';

const b3 = await createBox3D();
const world = b3.createWorld({ gravity: [0, -9.81, 0] });

const ground = world.createBody({ type: 'static' });
world.addBox(ground, [50, 0.5, 50], { friction: 0.8 });

// 6×10 brick wall → one flat transform buffer (7 floats/body)
const buffer = new TransformBuffer(64);
const bricks = [];
for (let row = 0; row < 10; row++) {
  for (let col = 0; col < 6; col++) {
    const body = world.createBody({
      type: 'dynamic',
      position: [col * 1.02 - 3, 0.5 + row * 0.52, 0],
    });
    world.addBox(body, [0.5, 0.25, 0.25], { density: 2, friction: 0.7 });
    bricks.push(body);
    buffer.add(body);
  }
}

const sleep = new SleepManager(world, { sweepIntervalSec: 2, moveThreshold: 0.01 });
sleep.watch(bricks, buffer);

// NB: vertexColors must stay false → else black instances
const mesh = new THREE.InstancedMesh(
  new THREE.BoxGeometry(1, 0.5, 0.5),
  new THREE.MeshStandardMaterial(),
  bricks.length,
);

const stepper = new FixedStepper(); // 1/60, 4 substeps

function frame(delta) {
  const stepped = stepper.advance(delta, (dt) => {
    world.step(dt);
    sleep.forceSleepSettled();
    sleep.sweep(stepper.simTime);
  });
  if (stepped) {
    buffer.rebuild();
    buffer.readInto(world);
    writeTransformsToInstancedMesh(mesh, buffer);
  }
}`;

interface Box3DProps {
  onBack: () => void;
}

export default function Box3D({ onBack }: Box3DProps) {
  const viewedRef = useRef(false);

  useEffect(() => {
    initAnalytics('box3d');
    if (viewedRef.current) return;
    viewedRef.current = true;
    track('landing_view', {
      path: '/box3d',
      ...getUtmMetadata(),
    });
  }, []);

  const onGithubClick = () => {
    track('github_click', {
      path: '/box3d',
      target: GITHUB_URL,
      ...getUtmMetadata(),
    });
  };

  const onDemoClick = (demoPath: string) => {
    track('demo_click', {
      path: '/box3d',
      demo: demoPath,
      ...getUtmMetadata(),
    });
  };

  return (
    <div className="box3d-page" data-testid="box3d-landing">
      <header className="box3d-topbar">
        <button type="button" className="box3d-back" onClick={onBack}>
          ← Back
        </button>
        <span className="box3d-topbar-brand">three-box3d</span>
        <a
          className="box3d-topbar-gh"
          href={GITHUB_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onGithubClick}
        >
          GitHub
        </a>
      </header>

      <main className="box3d-main">
        {/* Hero */}
        <section className="box3d-hero">
          <span className="box3d-badge" data-testid="box3d-prealpha-badge">
            Pre-alpha · building in public
          </span>
          <h1 className="box3d-title">three-box3d</h1>
          <p className="box3d-pitch">
            3D physics for three.js and React Three Fiber, powered by Erin
            Catto&apos;s box3d.
          </p>
          <p className="box3d-subpitch">
            Typed API · island-aware sleep · InstancedMesh transform sync ·
            fixed-timestep runtime. Not on npm yet — star the repo and dogfood
            the live demos while the API hardens.
          </p>
          <div className="box3d-ctas">
            <a
              className="box3d-cta box3d-cta-primary"
              href={GITHUB_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="box3d-github-cta"
              onClick={onGithubClick}
            >
              ★ Star on GitHub
            </a>
            <a
              className="box3d-cta box3d-cta-secondary"
              href="/wrecking-yard"
              data-testid="box3d-demo-cta"
              onClick={() => onDemoClick('/wrecking-yard')}
            >
              See it live →
            </a>
          </div>
        </section>

        {/* What it is */}
        <section className="box3d-section">
          <h2 className="box3d-h2">What it is</h2>
          <p className="box3d-body">
            <strong>three-box3d</strong> is the three.js / R3F integration layer
            for box3d — Erin Catto&apos;s 3D successor to Box2D. Not a bare WASM
            binding dump: typed handles, three.js-native helpers, and the
            production-hardening a real scene needs.
          </p>
          <ul className="box3d-feature-list">
            <li>
              <strong>Island-aware sleep</strong> — settled structures cost ~0
              step time via SleepManager sweeps.
            </li>
            <li>
              <strong>InstancedMesh sync</strong> — bulk transform buffers → one
              matrix write, one draw call.
            </li>
            <li>
              <strong>Fixed-timestep runtime</strong> — FixedStepper with
              substeps and death-spiral clamp.
            </li>
            <li>
              <strong>Three packages</strong> — <code>box3d-web</code> (core),{' '}
              <code>three-box3d</code> (adapter), <code>r3f-box3d</code>{' '}
              (hooks). Pre-alpha; APIs may break until v1.
            </li>
          </ul>
        </section>

        {/* Live demos */}
        <section className="box3d-section">
          <h2 className="box3d-h2">Dogfooded live</h2>
          <p className="box3d-body">
            These routes on this app run the same library. Click through — that
            is the real demo, not a mockup.
          </p>
          <div className="box3d-demo-grid">
            {DEMO_ROUTES.map((demo) => (
              <a
                key={demo.path}
                className="box3d-demo-card"
                href={demo.path}
                onClick={() => onDemoClick(demo.path)}
              >
                <span className="box3d-demo-label">{demo.label}</span>
                <span className="box3d-demo-path">{demo.path}</span>
                <span className="box3d-demo-blurb">{demo.blurb}</span>
              </a>
            ))}
          </div>
        </section>

        {/* Gotchas teaser */}
        <section className="box3d-section box3d-gotchas">
          <h2 className="box3d-h2">Gotchas you&apos;ll hit anyway</h2>
          <p className="box3d-body">
            The repo&apos;s Gotchas doc is paid-for debugging time, not marketing
            fluff. Teaser: set{' '}
            <code>vertexColors: true</code> on an InstancedMesh material without
            a per-vertex color attribute and every instance renders{' '}
            <strong>black</strong>. That trap hit three separate meshes in this
            app alone.
          </p>
          <a
            className="box3d-link"
            href={GOTCHAS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onGithubClick}
          >
            Read the Gotchas doc on GitHub →
          </a>
        </section>

        {/* Code peek */}
        <section className="box3d-section">
          <div className="box3d-code-header">
            <h2 className="box3d-h2">Code peek — brick wall</h2>
            <span className="box3d-api-badge">Pre-alpha API · may change</span>
          </div>
          <p className="box3d-body">
            Quickstart from the README: wall + InstancedMesh + sleep + fixed
            step. Packages are not published to npm yet — clone the monorepo.
          </p>
          <pre className="box3d-code" data-testid="box3d-code-peek">
            <code>{QUICKSTART_SNIPPET}</code>
          </pre>
        </section>

        {/* Footer CTAs */}
        <section className="box3d-footer-cta">
          <p className="box3d-footer-line">
            Pre-alpha. MIT. Built in public. Stars help more than silence.
          </p>
          <div className="box3d-ctas">
            <a
              className="box3d-cta box3d-cta-primary"
              href={GITHUB_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onGithubClick}
            >
              ★ Star on GitHub
            </a>
            <a
              className="box3d-cta box3d-cta-secondary"
              href="/physics-playground"
              onClick={() => onDemoClick('/physics-playground')}
            >
              Open Physics Playground →
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}
